<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import { Chart, registerables } from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";

Chart.register(...registerables, annotationPlugin);

const props = defineProps({
  chartData: Object
});

const store = useStore();
const isDarkMode = computed(() => !!store.state.isDarkMode);
const canvasRef = ref(null);
const canvasWrapRef = ref(null);
const tradeBubbles = ref([]);
const hoveredBubble = ref(-1);
let chartInstance = null;
let lastBubbleLayout = "";

const theme = computed(() => {
  if (isDarkMode.value) {
    return {
      title: "#FF8A4C",
      tick: "#94A3B8",
      grid: "rgba(51, 65, 85, 0.65)",
      startLine: "rgba(148, 163, 184, 0.4)",
      lastBg: "#0F172A",
      lastFg: "#E2E8F0",
      pointBorder: "#0F172A",
      tooltipBg: "rgba(15, 23, 42, 0.94)",
      up: "#34D399",
      down: "#F87171"
    };
  }
  return {
    title: "#334155",
    tick: "#94A3B8",
    grid: "#F1F5F9",
    startLine: "rgba(148, 163, 184, 0.55)",
    lastBg: "#1E293B",
    lastFg: "#ffffff",
    pointBorder: "#ffffff",
    tooltipBg: "#1E293B",
    up: "#2dce89",
    down: "#f5365c"
  };
});

const hexToRgbA = (hex, alpha) => {
  const h = String(hex || "").replace("#", "");
  if (h.length !== 6) return `rgba(40, 112, 237, ${alpha})`;
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

const parseNum = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isNaN(value) ? null : value;
  const n = parseFloat(String(value).replace(/\s+/g, "").replace(",-", "").replace(",", "."));
  return Number.isNaN(n) ? null : n;
};

const formatMoney = (value) => {
  const num = parseNum(value);
  if (num == null) return "0";
  const hasDecimals = num % 1 !== 0;
  return num.toLocaleString("cs-CZ", {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2
  });
};

const asArray = (value) => (Array.isArray(value) ? value : value ? Object.values(value) : []);

const parsed = computed(() => {
  const incoming = props.chartData;
  if (!incoming) return null;
  const mainData = incoming.Data || incoming;
  const chartInfo = mainData?.Chart;
  const titleInfo = mainData?.Title || {};
  if (!chartInfo || !chartInfo.cfg) return null;

  const labels = asArray(chartInfo.cfg.labels);
  const rawDatasets = chartInfo.datasets || chartInfo.cfg.datasets || [];
  const dataset = asArray(rawDatasets)[0];
  if (!dataset) return null;

  const prices = asArray(dataset.data).map(parseNum);
  if (!labels.length || !prices.length) return null;

  let minVal = Infinity;
  let maxVal = -Infinity;
  let minIdx = 0;
  let maxIdx = 0;
  let lastIdx = -1;
  prices.forEach((val, idx) => {
    if (val == null) return;
    lastIdx = idx;
    if (val < minVal) {
      minVal = val;
      minIdx = idx;
    }
    if (val > maxVal) {
      maxVal = val;
      maxIdx = idx;
    }
  });
  if (lastIdx < 0) return null;

  const firstVal = prices.find((v) => v != null);
  const lastVal = prices[lastIdx];
  const currentPrice = parseNum(titleInfo.Price) ?? lastVal;
  const pctDiff = parseNum(titleInfo.PctDiff);
  const rawColor = String(titleInfo.Color || dataset.borderColor || "#FF9900").trim();
  const color = rawColor.startsWith("#") ? rawColor : `#${rawColor}`;

  const trades = asArray(mainData.Trades || incoming.Trades).map((row) => ({
    d: String(row?.d || "").trim(),
    t: String(row?.t || "").trim(),
    a: parseNum(row?.a)
  })).filter((row) => row.d);

  return {
    name: titleInfo.Name || dataset.label || "Akcie",
    isin: titleInfo.ISIN || "",
    color,
    labels,
    prices,
    trades,
    firstVal,
    lastVal,
    lastIdx,
    minVal,
    minIdx,
    maxVal,
    maxIdx,
    currentPrice,
    pctDiff,
    priceDiff: parseNum(titleInfo.PriceDiff),
    min90: parseNum(titleInfo.Min90),
    max90: parseNum(titleInfo.Max90),
    lastDate: labels[lastIdx] || ""
  };
});

const formatKurzDate = (raw) => {
  const text = String(raw || "").trim();
  if (!text) return "";
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return `${Number(iso[3])}. ${Number(iso[2])}. ${iso[1]}`;
  }
  return text;
};

const changeTone = computed(() => {
  const pct = parsed.value?.pctDiff;
  const amt = parsed.value?.priceDiff;
  const n = pct != null ? pct : amt;
  if (n == null || Math.abs(n) < 0.005) return "is-flat";
  return n > 0 ? "is-up" : "is-down";
});

const formatPctLabel = (value) => {
  const n = parseNum(value);
  if (n == null) return "";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`;
};

const formatQty = (value) => {
  const num = parseNum(value);
  if (num == null) return "0";
  const abs = Math.abs(num);
  if (abs % 1 === 0) return String(Math.round(abs));
  return abs.toLocaleString("cs-CZ", { maximumFractionDigits: 2 });
};

const wrapLabel = (text, max = 26) => {
  const words = String(text || "").replace(/\s+/g, " ").trim().split(" ").filter(Boolean);
  if (!words.length) return [""];
  const lines = [];
  let line = "";
  words.forEach((word) => {
    const next = line ? `${line} ${word}` : word;
    if (next.length > max && line) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  });
  if (line) lines.push(line);
  return lines.slice(0, 4);
};

const tradeLines = (trade) => {
  const lines = wrapLabel(trade.t || "");
  const qty = formatQty(trade.a);
  if (qty && !/ks/i.test(String(trade.t || ""))) lines.push(`${qty} ks`);
  return lines;
};

const tradeColor = (trade) => {
  const text = String(trade.t || "");
  if (/short/i.test(text)) return "#6D28D9";
  if (/prodej|uzavřen|redukce/i.test(text)) return "#AB4058";
  return "#065F46";
};

const parseDdMm = (value) => {
  const m = String(value || "").trim().match(/^(\d{1,2})\.(\d{1,2})/);
  if (!m) return null;
  return { day: Number(m[1]), month: Number(m[2]) };
};

const labelDates = (labels) => {
  let year = new Date().getFullYear();
  const dates = new Array(labels.length);
  for (let i = labels.length - 1; i >= 0; i--) {
    const p = parseDdMm(labels[i]);
    if (!p) continue;
    let dt = new Date(year, p.month - 1, p.day);
    if (i < labels.length - 1 && dates[i + 1] && dt > dates[i + 1]) {
      year -= 1;
      dt = new Date(year, p.month - 1, p.day);
    }
    dates[i] = dt;
  }
  return dates;
};

const indexForTrade = (labels, trade) => {
  if (!labels.length) return -1;
  const exact = labels.findIndex((label) => String(label) === String(trade.d || "").trim());
  if (exact >= 0) return exact;

  const dates = labelDates(labels);
  const lastDate = dates[dates.length - 1];
  const parsed = parseDdMm(trade.d);
  let tradeDate = null;
  if (parsed) {
    const y = lastDate ? lastDate.getFullYear() : new Date().getFullYear();
    tradeDate = new Date(y, parsed.month - 1, parsed.day);
    if (lastDate && tradeDate < dates[0] && parsed.month > lastDate.getMonth() + 1) {
      tradeDate = new Date(y - 1, parsed.month - 1, parsed.day);
    }
  }
  if (!tradeDate) return labels.length - 1;

  let best = -1;
  for (let i = 0; i < dates.length; i++) {
    if (dates[i] && dates[i] <= tradeDate) best = i;
  }
  return best >= 0 ? best : labels.length - 1;
};

const xShift = (idx, n, wide = false) => {
  if (n <= 1) return 0;
  const edge = wide ? 72 : 48;
  if (idx <= 0) return edge;
  if (idx >= n - 1) return -edge;
  return 0;
};

const near = (idxA, valA, idxB, valB, n, range) => {
  const idxClose = Math.abs(idxA - idxB) <= Math.max(2, Math.round(n * 0.05));
  const valClose = Math.abs(valA - valB) / range < 0.14;
  return { idxClose, valClose, overlap: idxClose && valClose };
};

const niceYAxis = (low, high) => {
  const lo = Number(low);
  const hi = Number(high);
  const span = Math.max(hi - lo, 1);
  const paddedLo = lo - span * 0.06;
  const paddedHi = hi + span * 0.06;
  const raw = Math.max(paddedHi - paddedLo, 1);
  const mag = Math.pow(10, Math.floor(Math.log10(raw / 6)));
  const candidates = [1, 2, 2.5, 5, 10].map((n) => n * mag);
  const step = candidates.find((n) => raw / n <= 8) || candidates[candidates.length - 1];
  let min = Math.floor(paddedLo / step) * step;
  let max = Math.ceil(paddedHi / step) * step;
  if (max <= min) max = min + step;
  return { min, max, step };
};

const drawChart = async () => {
  const info = parsed.value;
  if (!info) return;
  await nextTick();
  if (!canvasRef.value) return;

  const ctx = canvasRef.value.getContext("2d");
  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }

  const t = theme.value;
  const n = info.labels.length;
  const range = Math.max(info.maxVal - info.minVal, 1);
  const vsMax = near(info.maxIdx, info.maxVal, info.lastIdx, info.lastVal, n, range);
  const vsMin = near(info.minIdx, info.minVal, info.lastIdx, info.lastVal, n, range);
  const lastAtPeak = vsMax.overlap || (info.maxVal - info.lastVal) / range < 0.08;
  const low = Math.min(info.minVal, info.currentPrice);
  const high = Math.max(info.maxVal, info.currentPrice);
  const { min: yMin, max: yMax, step: yStep } = niceYAxis(low, high);

  const gradient = ctx.createLinearGradient(0, 0, 0, 360);
  gradient.addColorStop(0, hexToRgbA(info.color, isDarkMode.value ? 0.28 : 0.22));
  gradient.addColorStop(1, hexToRgbA(info.color, 0));

  const lastLines = [info.name, `${formatMoney(info.lastVal)} Kč`];
  if (vsMax.overlap) lastLines.push("na maximu");

  const tradeIdx = new Set();
  const tradesOnChart = (info.trades || []).map((trade, i) => {
    const idx = indexForTrade(info.labels, trade);
    if (idx < 0 || info.prices[idx] == null) return null;
    tradeIdx.add(idx);
    return { ...trade, idx, y: info.prices[idx], i };
  }).filter(Boolean);

  const annotations = {
    nowLine: {
      type: "line",
      yMin: info.currentPrice,
      yMax: info.currentPrice,
      borderColor: t.startLine,
      borderWidth: 1,
      borderDash: [6, 4]
    }
  };

  const layoutBubbles = (chart) => {
    const xScale = chart.scales.x;
    const yScale = chart.scales.y;
    if (!xScale || !yScale) return;

    const pixel = (idx, y) => {
      const px = xScale.getPixelForValue(info.labels[idx]);
      const py = yScale.getPixelForValue(y);
      let xOff = 0;
      if (idx <= 0) xOff = 36;
      if (idx >= n - 1) xOff = -36;
      return { x: px + xOff, y: py };
    };

    const next = [];

    const lastPos = pixel(info.lastIdx, info.lastVal);
    next.push({
      lines: lastLines,
      color: t.lastBg,
      border: info.color,
      x: lastPos.x,
      y: lastPos.y,
      above: !lastAtPeak,
      title: lastLines.join(" · "),
      kind: "stat"
    });

    if (!vsMax.overlap && info.maxIdx !== info.lastIdx) {
      const pos = pixel(info.maxIdx, info.maxVal);
      next.push({
        lines: [`Max ${formatMoney(info.maxVal)} Kč`],
        color: "#065F46",
        x: pos.x + (vsMax.idxClose ? -28 : 0),
        y: pos.y,
        above: !vsMax.idxClose,
        title: "Maximum",
        kind: "stat"
      });
    }

    if (!vsMin.overlap && info.minIdx !== info.lastIdx && info.minIdx !== info.maxIdx) {
      const pos = pixel(info.minIdx, info.minVal);
      next.push({
        lines: [`Min ${formatMoney(info.minVal)} Kč`],
        color: "#AB4058",
        x: pos.x,
        y: pos.y,
        above: false,
        title: "Minimum",
        kind: "stat"
      });
    }

    const daySeen = {};
    tradesOnChart.forEach((trade) => {
      const seen = daySeen[trade.idx] || 0;
      daySeen[trade.idx] = seen + 1;
      const pos = pixel(trade.idx, trade.y);
      const nearTop = (trade.y - yMin) / Math.max(yMax - yMin, 1) > 0.58;
      const above = seen % 2 === 0 ? !nearTop : nearTop;
      next.push({
        lines: tradeLines(trade),
        color: tradeColor(trade),
        x: pos.x,
        y: pos.y,
        above,
        title: trade.t,
        kind: "trade"
      });
    });

    const key = JSON.stringify(next);
    if (key === lastBubbleLayout) return;
    lastBubbleLayout = key;
    tradeBubbles.value = next;
  };

  hoveredBubble.value = -1;
  lastBubbleLayout = "";
  chartInstance = new Chart(ctx, {
    type: "line",
    data: {
      labels: info.labels,
      datasets: [
        {
          label: info.name,
          data: info.prices,
          borderColor: info.color,
          backgroundColor: gradient,
          borderWidth: 3,
          tension: 0.35,
          fill: true,
          spanGaps: true,
          pointHoverRadius: 6,
          pointRadius: info.prices.map((_, i) => {
            if (tradeIdx.has(i)) return 5;
            if (i === info.lastIdx) return 5;
            if (i === info.maxIdx || i === info.minIdx) return 4;
            return 0;
          }),
          pointBackgroundColor: info.prices.map((_, i) => {
            if (tradeIdx.has(i)) return "#FF8A4C";
            if (i === info.minIdx) return "#AB4058";
            if (i === info.maxIdx) return "#065F46";
            return info.color;
          }),
          pointBorderColor: t.pointBorder,
          pointBorderWidth: 2
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      layout: {
        padding: { top: 36, right: 28, bottom: 28, left: 8 }
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          mode: "index",
          intersect: false,
          backgroundColor: t.tooltipBg,
          titleColor: "#ffffff",
          bodyColor: "#E2E8F0",
          padding: 12,
          borderRadius: 8,
          callbacks: {
            label: (ctx) => {
              const y = ctx.parsed.y;
              if (y == null) return " –";
              return ` Cena: ${formatMoney(y)} Kč`;
            },
            afterLabel: (ctx) => {
              const i = ctx.dataIndex;
              const extra = [];
              const prev = i > 0 ? info.prices[i - 1] : null;
              const y = ctx.parsed.y;
              if (prev != null && y != null) {
                const diff = y - prev;
                const pct = prev !== 0 ? (diff / prev) * 100 : 0;
                const sign = diff > 0 ? "+" : "";
                extra.push(` ${sign}${formatMoney(diff)} Kč (${sign}${pct.toFixed(2)} %)`);
              }
              tradesOnChart
                .filter((trade) => trade.idx === i)
                .forEach((trade) => extra.push(` ${trade.t}`));
              return extra;
            }
          }
        },
        annotation: {
          clip: false,
          annotations
        }
      },
      onResize: (chart) => layoutBubbles(chart),
      interaction: { intersect: false, mode: "index" },
      scales: {
        x: {
          type: "category",
          grid: { display: false },
          ticks: {
            color: t.tick,
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 8
          }
        },
        y: {
          type: "linear",
          min: yMin,
          max: yMax,
          grid: { color: t.grid, drawBorder: false },
          ticks: {
            color: t.tick,
            font: { size: 11 },
            padding: 8,
            stepSize: yStep,
            includeBounds: false,
            callback: (value) => {
              const n = Number(value);
              if (!Number.isFinite(n)) return "";
              const decimals = yStep < 1 ? 2 : yStep < 10 && n % 1 !== 0 ? 1 : 0;
              return `${n.toLocaleString("cs-CZ", {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals
              })} Kč`;
            }
          }
        }
      }
    }
  });
  layoutBubbles(chartInstance);
  nextTick(() => {
    if (chartInstance) layoutBubbles(chartInstance);
  });
};

const pickHoveredBubble = (e) => {
  const wrap = canvasWrapRef.value;
  if (!wrap || !tradeBubbles.value.length) {
    hoveredBubble.value = -1;
    return;
  }
  const wrapRect = wrap.getBoundingClientRect();
  const mx = e.clientX - wrapRect.left;
  const my = e.clientY - wrapRect.top;
  const nodes = wrap.querySelectorAll(".trade-bubble");
  let best = -1;
  let bestD = 110;
  nodes.forEach((el, i) => {
    const r = el.getBoundingClientRect();
    const left = r.left - wrapRect.left;
    const top = r.top - wrapRect.top;
    const cx = left + r.width / 2;
    const cy = top + r.height / 2;
    const inBox =
      mx >= left - 10 &&
      mx <= left + r.width + 10 &&
      my >= top - 10 &&
      my <= top + r.height + 10;
    const d = Math.hypot(mx - cx, my - cy);
    const score = inBox ? d * 0.3 : d;
    if (score < bestD) {
      bestD = score;
      best = i;
    }
  });
  hoveredBubble.value = bestD < 110 ? best : -1;
};

const clearHoveredBubble = () => {
  hoveredBubble.value = -1;
};

watch([parsed, isDarkMode], drawChart, { immediate: true });

onBeforeUnmount(() => {
  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }
  tradeBubbles.value = [];
  hoveredBubble.value = -1;
});
</script>

<template>
  <div v-if="parsed" class="isin-chart" :class="{ 'isin-chart-dark': isDarkMode }">
    <div class="isin-chart-meta">
      <div class="isin-stat-group">
        <span class="isin-stat isin-stat-kurz">
          <strong>{{ formatMoney(parsed.currentPrice) }} Kč</strong>
          <small>KURZ{{ parsed.lastDate ? ` (${formatKurzDate(parsed.lastDate)})` : "" }}</small>
        </span>
        <span
          v-if="parsed.pctDiff != null"
          class="isin-stat isin-stat-change"
          :class="changeTone"
        >
          <strong>{{ formatPctLabel(parsed.pctDiff) }}</strong>
          <small>ZMĚNA</small>
        </span>
      </div>
      <div class="isin-stat-group isin-stat-group-right">
        <span v-if="parsed.min90 != null" class="isin-stat isin-stat-min">
          <strong>{{ formatMoney(parsed.min90) }} Kč</strong>
          <small>90denní minimum</small>
        </span>
        <span v-if="parsed.max90 != null" class="isin-stat isin-stat-max">
          <strong>{{ formatMoney(parsed.max90) }} Kč</strong>
          <small>90denní maximum</small>
        </span>
      </div>
    </div>
    <div
      class="isin-chart-canvas"
      ref="canvasWrapRef"
      @mousemove="pickHoveredBubble"
      @mouseleave="clearHoveredBubble"
    >
      <canvas ref="canvasRef"></canvas>
      <div
        v-for="(bubble, i) in tradeBubbles"
        :key="`${bubble.title}-${i}`"
        class="trade-bubble"
        :class="{
          'is-front': hoveredBubble === i,
          'is-above': bubble.above,
          'is-dim': hoveredBubble >= 0 && hoveredBubble !== i,
          'is-stat': bubble.kind === 'stat'
        }"
        :style="{
          left: `${bubble.x}px`,
          top: `${bubble.y}px`,
          backgroundColor: bubble.color,
          borderColor: bubble.border || 'transparent',
          color: bubble.kind === 'stat' ? (isDarkMode ? '#E2E8F0' : '#ffffff') : '#ffffff',
          zIndex: hoveredBubble === i ? 12 : 4
        }"
        :title="bubble.title"
      >
        <div v-for="(line, li) in bubble.lines" :key="li">{{ line }}</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.isin-chart {
  width: 100%;
}

.isin-chart-meta {
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: flex-end;
  gap: 24px;
  padding: 0 8px 10px;
}

.isin-stat-group {
  display: flex;
  align-items: flex-end;
  gap: 1.65rem;
}

.isin-stat-group-right {
  gap: 14px;
  margin-left: auto;
}

.isin-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
}

.isin-stat-group-right .isin-stat {
  text-align: right;
}

.isin-stat small {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #64748B;
}

.isin-stat-kurz small,
.isin-stat-change small {
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.isin-stat strong {
  font-size: 0.95rem;
  font-weight: 800;
  color: #0F172A;
}

.isin-stat-kurz strong {
  font-size: 2.05rem;
  letter-spacing: -0.03em;
  line-height: 1.05;
}

.isin-stat-change.is-up,
.isin-stat-change.is-up small {
  color: #15803D;
}

.isin-stat-change.is-down,
.isin-stat-change.is-down small {
  color: #B91C1C;
}

.isin-stat-change.is-flat,
.isin-stat-change.is-flat small {
  color: #64748B;
}

.isin-stat-min strong {
  color: #AB4058;
}

.isin-stat-max strong {
  color: #065F46;
}

.isin-chart-canvas {
  position: relative;
  width: 100%;
  height: 420px;
}

.trade-bubble {
  position: absolute;
  transform: translate(-50%, 8px);
  max-width: 220px;
  padding: 5px 8px;
  border-radius: 6px;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.25;
  pointer-events: none;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.28);
  transition: box-shadow 0.12s ease, transform 0.12s ease;
}

.trade-bubble.is-above {
  transform: translate(-50%, calc(-100% - 8px));
}

.trade-bubble.is-stat {
  border: 1px solid rgba(255, 255, 255, 0.25);
}

.trade-bubble.is-front {
  z-index: 12;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.45);
  transform: translate(-50%, 8px) scale(1.04);
}

.trade-bubble.is-front.is-above {
  transform: translate(-50%, calc(-100% - 8px)) scale(1.04);
}

.isin-chart-dark .isin-stat strong {
  color: #E2E8F0;
}

.isin-chart-dark .isin-stat small {
  color: #94A3B8;
}

.isin-chart-dark .isin-stat-change.is-up,
.isin-chart-dark .isin-stat-change.is-up small {
  color: #34D399;
}

.isin-chart-dark .isin-stat-change.is-down,
.isin-chart-dark .isin-stat-change.is-down small {
  color: #F87171;
}

.isin-chart-dark .isin-stat-change.is-flat,
.isin-chart-dark .isin-stat-change.is-flat small {
  color: #94A3B8;
}

.isin-chart-dark .isin-stat-min strong {
  color: #E07A82;
}

.isin-chart-dark .isin-stat-max strong {
  color: #34D399;
}
</style>

